<template>
    <div v-if="previewUrl" class="indesign-banner">
        <img
            :src="previewUrl"
            :alt="id"
            class="preview"
            :class="{ 'preview--loading': isLoading }"
            @load="onImageLoad"
            @error="onImageError"
        />

        <InDesignFrameOverlay
            v-if="isOverlayVisible"
            :bounds="displayBounds"
            :page-width="pageWidth"
            :page-height="pageHeight"
            :offset-x="selectedOffset.x"
            :offset-y="selectedOffset.y"
            :label="selectedLabel"
            @commit="onOverlayCommit"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch, type EmitFn } from "vue";
import { useRoute } from "vue-router";
import { useStore } from "vuex";
import type { S3FileLocation } from "shared-types/indesign";
import type { InDesignMasterTemplate } from "shared-types";

import type { InDesignComputedOverwrite, UpdatedEditable } from "@/types/types";
import { BannerEvents } from "@/enums/banners";
import { requestIndesignPreview } from "@/utils/CreativeIntelligenceApi/indesign";
import { getRenditionPreviewRequestBody } from "@/services/InDesignPreview";
import InDesignFrameOverlay from "@/components/Previews/InDesignFrameOverlay.vue";
import { useEditor } from "@/composables/useEditor";
import { useOverwrite } from "@/composables/useOverwrite";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { EditorEvents } from "@/enums/editor";
import { EditableEvent } from "@/enums/editables";
import { EditorAction } from "@/store/modules/editor";
import bus from "@/bus";
import { toPoints } from "shared-utils/utils/indesignUnits";

function isAbortError(error: unknown) {
    return error instanceof Error && error.name === "AbortError";
}

const emit = defineEmits(["loading", BannerEvents.BannerLoaded, BannerEvents.BannerError, BannerEvents.BannerWarnings]);

const { id, previewValues, contentClickable } = defineProps<{
    id: string;
    previewValues: string;
    contentClickable?: boolean;
}>();

const route = useRoute();
const store = useStore();
const previewUrl = ref<string | null>(null);
// Where the render actually put each overridden frame, in points. A frame that grew to fit its
// text is no longer the size the template recorded.
const renderedFrames = ref<Record<string, { top: number; left: number; bottom: number; right: number }>>({});
const isLoading = ref(false);
let activePreviewController: AbortController | null = null;

const data = computed(() => {
    return JSON.parse(previewValues) as {
        clientId?: string;
        computedOverwrites?: InDesignComputedOverwrite[];
        targetSize?: {
            width?: number;
            height?: number;
            unit?: string;
        };
        masterTemplate: Omit<InDesignMasterTemplate, "sourceDocuments"> & {
            sourceDocuments: S3FileLocation[];
            measurementUnit?: string;
            parentWidth?: number;
            parentHeight?: number;
        };
    };
});

const { editedEditable } = useEditor();
const { getOverwrite } = useOverwrite();

// Routed through the bus rather than four levels of props
const emitToBus = (event: string, payload: UpdatedEditable) => {
    if (event === EditableEvent.Update) {
        bus.$emit(EditorEvents.UpdateEditableSettings, payload);
    }
};

const { updateSettings, getOwnSettings } = useEditableMethods({ emit: emitToBus as unknown as EmitFn });

const masterTemplateId = computed(() => data.value.masterTemplate._id);

// Frame bounds are recorded in the document's ruler unit, the same one width and height use,
// so the outline is placed in that unit. Only the stored offset is in points, because that is
// what the render script applies.
const pageWidth = computed(() => Number(data.value.masterTemplate.width));
const pageHeight = computed(() => Number(data.value.masterTemplate.height));
const pointsPerUnit = computed(() => toPoints(1, data.value.masterTemplate.measurementUnit));

// A resized template inherits its parent's bounds, so scale them - approximately
function getBoundsScale(size: number, parentSize: number) {
    return parentSize > 0 && size > 0 ? size / parentSize : 1;
}

const boundsScaleX = computed(() =>
    getBoundsScale(Number(data.value.masterTemplate.width), Number(data.value.masterTemplate.parentWidth))
);
const boundsScaleY = computed(() =>
    getBoundsScale(Number(data.value.masterTemplate.height), Number(data.value.masterTemplate.parentHeight))
);

const renderedFrameBounds = computed(() => {
    const itemName = editedEditable.value?.editable.name;
    const rendered = itemName ? renderedFrames.value[itemName] : undefined;

    if (!rendered || pointsPerUnit.value <= 0) {
        return undefined;
    }

    return {
        top: rendered.top / pointsPerUnit.value,
        left: rendered.left / pointsPerUnit.value,
        bottom: rendered.bottom / pointsPerUnit.value,
        right: rendered.right / pointsPerUnit.value
    };
});

const storedFrameBounds = computed(() => {
    const layers = editedEditable.value?.editable.layers ?? [];
    const layer = layers.find(({ masterTemplateId: layerTemplateId }) => layerTemplateId === masterTemplateId.value);
    const bounds = layer?.value?.bounds;

    const edges = [bounds?.top, bounds?.left, bounds?.bottom, bounds?.right];

    if (!bounds || edges.some(edge => typeof edge !== "number")) {
        return undefined;
    }

    return { top: bounds.top, left: bounds.left, bottom: bounds.bottom as number, right: bounds.right as number };
});

// This banner's template, not whichever the settings pane is showing
const selectedSettings = computed(() => {
    if (!editedEditable.value) {
        return undefined;
    }

    return getOverwrite(
        editedEditable.value.editable,
        editedEditable.value.groupsAffectingEditable,
        masterTemplateId.value
    )?.settings;
});

const selectedOffset = computed(() => ({
    x: Number(selectedSettings.value?.offsetX ?? 0) / pointsPerUnit.value,
    y: Number(selectedSettings.value?.offsetY ?? 0) / pointsPerUnit.value
}));

const selectedLabel = computed(() => editedEditable.value?.editable.name ?? "");

const displayBounds = computed(() => {
    if (renderedFrameBounds.value) {
        return renderedFrameBounds.value;
    }

    const stored = storedFrameBounds.value;

    if (!stored) {
        return undefined;
    }

    const { x, y } = selectedOffset.value;

    return {
        top: stored.top * boundsScaleY.value + y,
        left: stored.left * boundsScaleX.value + x,
        bottom: stored.bottom * boundsScaleY.value + y,
        right: stored.right * boundsScaleX.value + x
    };
});

const isOverlayVisible = computed(
    () => !!contentClickable && !isLoading.value && !!displayBounds.value && pageWidth.value > 0 && pageHeight.value > 0
);

function toStoredOffset(value: number) {
    return Math.round(value * pointsPerUnit.value * 100) / 100;
}

// The overlay works in page units; the script applies offsets in points
function onOverlayCommit({ offsetX, offsetY }: { offsetX: number; offsetY: number }) {
    if (!editedEditable.value) {
        return;
    }

    const { editable, groupsAffectingEditable } = editedEditable.value;
    delete renderedFrames.value[editable.name];

    updateSettings({
        editable,
        settings: {
            // Only what this size sets itself: writing the inherited values too would pin a copy
            // here, and later changes for every size would stop reaching it
            ...(getOwnSettings(editable, groupsAffectingEditable, masterTemplateId.value) ?? {}),
            offsetX: toStoredOffset(offsetX),
            offsetY: toStoredOffset(offsetY)
        },
        groupsAffectingEditable,
        // A nudge on one size must not move the others
        templateId: masterTemplateId.value
    });

    // Moving the frame on one size is editing that size, so the settings pane follows it there.
    // Left on the group, the next size or tracking change would land on every size instead.
    if (editedEditable.value.masterTemplateId !== masterTemplateId.value) {
        void store.dispatch(EditorAction.SetEditedEditable, {
            editable,
            masterTemplateId: masterTemplateId.value,
            groupsAffectingEditable
        });
    }
}

const clientId = computed(() => data.value.clientId || (route.params.clientId as string));

const sourceDocuments = computed(() => {
    return Array.isArray(data.value.masterTemplate.sourceDocuments) ? data.value.masterTemplate.sourceDocuments : [];
});

function cancelActivePreviewRequest() {
    activePreviewController?.abort();
    activePreviewController = null;
}

const renditionRequestBody = computed(() =>
    getRenditionPreviewRequestBody({
        clientId: clientId.value,
        computedOverwrites: data.value.computedOverwrites,
        masterTemplate: data.value.masterTemplate,
        sourceDocuments: sourceDocuments.value,
        targetSize: data.value.targetSize
    })
);

async function startPreviewRequest() {
    cancelActivePreviewRequest();

    const controller = new AbortController();
    activePreviewController = controller;

    try {
        isLoading.value = true;
        emit("loading", true);

        const preview = await requestIndesignPreview({
            signal: controller.signal,
            clientId: clientId.value,
            sourceDocuments: sourceDocuments.value,
            renditionRequestBody: renditionRequestBody.value
        });

        const isSameImage = previewUrl.value === preview.url;

        previewUrl.value = preview.url;
        renderedFrames.value = Object.fromEntries(preview.appliedFrames.map(frame => [frame.itemName, frame.bounds]));
        emit(BannerEvents.BannerWarnings, preview.warnings);

        // An unchanged src raises no load event, and the image would stay hidden behind the spinner
        if (isSameImage) {
            onImageLoad();
        }
    } catch (error) {
        if (isAbortError(error)) {
            return;
        }

        emit(BannerEvents.BannerError, error instanceof Error ? error.message : "Failed to request InDesign preview");
    } finally {
        if (activePreviewController === controller) {
            activePreviewController = null;
        }
    }
}

function onImageLoad() {
    isLoading.value = false;
    emit("loading", false);
    emit(BannerEvents.BannerLoaded);
}

function onImageError() {
    isLoading.value = false;
    emit(BannerEvents.BannerError, "There was an error loading the InDesign preview");
}

watch(
    renditionRequestBody,
    () => {
        void startPreviewRequest();
    },
    { deep: true, immediate: true }
);

onBeforeUnmount(() => {
    cancelActivePreviewRequest();
});
</script>

<style scoped>
.indesign-banner {
    position: relative;
}

.preview {
    display: block;
    width: 100%;
}

.preview--loading {
    visibility: hidden;
}
</style>
